import React from 'react';
import { PageHero } from '../components/ui/PageHero';
import { Container, Eyebrow, Heading } from '../components/ui/Section';
import { CtaBand } from '../components/ui/CtaBand';
import { ControlTowerDashboard } from '../components/features/ControlTowerDashboard';
import { OperationFlow } from '../components/features/OperationFlow';
import { SatelliteView } from '../components/features/SatelliteView';
import { Faq } from '../components/ui/Faq';
import { CONTROL_TOWER_FUNCTIONS, IMAGES } from '../data/solutions';
import { faqJsonLd, serviceJsonLd, useSeo } from '../utils/seo';
const FAQS = [{
  question: '¿Qué es el Control Tower de Rabemar?',
  answer: 'Una función operativa que centraliza coordinación, seguimiento de transportes, control documental, administración, pago a proveedores, HSE y medición de KPIs y SLA, con un Account Manager como punto de contacto único.'
}, {
  question: '¿Reemplaza al sistema que ya usamos?',
  answer: 'No necesariamente. El modelo puede integrar distintos proveedores de monitoreo satelital y trabajar sobre sistemas TMS/WMS propios o del cliente, consolidando la información en una vista operacional.'
}, {
  question: '¿Qué pasa cuando se detecta un desvío?',
  answer: 'Se registra el evento, se notifica a la coordinación y al cliente según el protocolo acordado, se ejecuta la acción correctiva y el caso queda trazado para el análisis de causas y el plan de acción.'
}];
const CRUMBS = [{
  name: 'Inicio',
  path: '/'
}, {
  name: 'Soluciones',
  path: '/soluciones'
}, {
  name: 'Control Tower',
  path: '/soluciones/control-tower'
}];
export function ControlTower() {
  useSeo({
    title: 'Control Tower logística | Seguimiento, KPIs y medición de SLA — Rabemar',
    description: 'Control Tower para operaciones logísticas: seguimiento de transportes, coordinación operativa, control documental, Call Center post carga, KPIs y medición de SLA.',
    path: '/soluciones/control-tower',
    image: IMAGES.controlTower,
    breadcrumbs: CRUMBS,
    jsonLd: [serviceJsonLd('Control Tower logístico', 'Seguimiento operacional, coordinación, control documental, evaluación de proveedores, KPIs y medición de SLA en una vista única.', '/soluciones/control-tower'), faqJsonLd(FAQS)]
  });
  return <>
      <PageHero eyebrow="03 — Control Tower" title={<>
            Vea su operación.
            <br data-id="element-596" data-mp="1ukwy9wz2kwg4" />
            Antes de que se convierta en un problema.
          </>} lead="Toda la coordinación, la documentación y los indicadores de su operación concentrados en una vista única, con responsables asignados y protocolos definidos." image={IMAGES.controlTower} imageAlt="Sala de control logístico con operadores frente a pantallas de seguimiento de flota y tableros de indicadores" crumbs={CRUMBS} ctaLabel="Solicitar propuesta" ctaService="Control Tower" data-id="element-595" data-mp="11u4vuhrevbkc" />

      <section className="bg-graphite-900 py-16 md:py-24" data-id="element-597" data-mp="2kvp1b6fpmwuj">
        <Container data-id="element-598" data-mp="2kt2u57wqejcu">
          <ControlTowerDashboard data-id="element-599" data-mp="2pth10mmljn9a" />
        </Container>
      </section>

      <section className="bg-white py-20 md:py-28" data-id="element-600" data-mp="2prns10oigqy4">
        <Container data-id="element-601" data-mp="2us20gju0y2wo">
          <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16" data-id="element-602" data-mp="2urigkxktd4g0">
            <div data-id="element-603" data-mp="2zkv414drb576">
              <Eyebrow data-id="element-604" data-mp="2zlegw6ojv3ec">Funciones integradas</Eyebrow>
              <Heading as="h2" size="md" className="mt-5" data-id="element-605" data-mp="34jfw2giuuikj">
                Un solo equipo para toda la coordinación.
              </Heading>
              <p className="mt-5 text-lg leading-relaxed text-graphite-500" data-id="element-606" data-mp="38ix49hpo1tte">
                El Control Tower no es sólo seguimiento: concentra las funciones administrativas, técnicas y de control
                que hacen que la operación avance sin fricción.
              </p>
            </div>
            <ul className="grid gap-px bg-mist-200 sm:grid-cols-2" data-id="element-607" data-mp="1j74on1h6sui8">
              {CONTROL_TOWER_FUNCTIONS.map(fn => <li key={fn} className="bg-white px-5 py-4 text-sm font-medium text-graphite-800" data-id="element-608" data-mp="1o6r4lioegouq">
                  {fn}
                </li>)}
            </ul>
          </div>
        </Container>
      </section>

      <section className="bg-mist-50 py-20 md:py-28" data-id="element-609" data-mp="3nm6oc4ztw9ed">
        <Container data-id="element-610" data-mp="3nlwychesa28o">
          <div className="max-w-3xl" data-id="element-611" data-mp="3nk3zvpebqobr">
            <Eyebrow data-id="element-612" data-mp="3jkleksilcb8e">Trazabilidad</Eyebrow>
            <Heading as="h2" size="md" className="mt-5" data-id="element-613" data-mp="3jlmouurs8tp2">
              De la carga al indicador, sin puntos ciegos.
            </Heading>
          </div>
          <div className="mt-10" data-id="element-614" data-mp="2qwpi9owxolc5">
            <OperationFlow tone="light" data-id="element-615" data-mp="2qxqys8wpy019" />
          </div>
        </Container>
      </section>

      <section className="bg-graphite-900 py-20 md:py-28" data-id="element-616" data-mp="34odstnrjb1r0">
        <Container data-id="element-617" data-mp="2zx44qrlmw6h8">
          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16" data-id="element-618" data-mp="2zvt593z81kvx">
            <div data-id="element-619" data-mp="2c6rrd17mdhj9">
              <Eyebrow tone="dark" data-id="element-620" data-mp="2c6889h68gn3n">Del dato a la decisión</Eyebrow>
              <Heading as="h2" size="lg" tone="dark" className="mt-5" data-id="element-621" data-mp="285of0oos63ms">
                Una vista única de toda la operación.
              </Heading>
              <p className="mt-6 text-lg leading-relaxed text-mist-200" data-id="element-622" data-mp="1y0zmo4j3bj8b">
                Rabemar puede centralizar la información operacional de distintas fuentes —transportistas, monitoreo
                satelital, documentación, reportes de servicio— para detectar desvíos y tomar acciones antes de que
                afecten el servicio.
              </p>
              <ul className="mt-9 space-y-4 border-t border-white/15 pt-7" data-id="element-623" data-mp="2grpn7ml6uty0">
                {[{
                t: 'Consolidar',
                d: 'Información dispersa reunida en un mismo tablero operativo.'
              }, {
                t: 'Detectar',
                d: 'Desvíos, demoras y documentación faltante identificados temprano.'
              }, {
                t: 'Actuar',
                d: 'Protocolos y responsables definidos para cada tipo de evento.'
              }, {
                t: 'Medir',
                d: 'Cumplimiento, SLA y desempeño de proveedores con evidencia.'
              }].map(step => <li key={step.t} className="flex gap-5" data-id="element-624" data-mp="10fakya3yf3cu">
                    <span className="w-24 shrink-0 font-display text-sm font-bold uppercase tracking-[0.1em] text-accent-400" data-id="element-625" data-mp="330xsfbxs1rzd">
                      {step.t}
                    </span>
                    <span className="text-sm leading-relaxed text-mist-200" data-id="element-626" data-mp="2talbdtp68gxz">{step.d}</span>
                  </li>)}
              </ul>
            </div>
            <SatelliteView data-id="element-627" data-mp="2o9ns1dsfdguw" />
          </div>
        </Container>
      </section>

      <section className="bg-white py-20 md:py-28" data-id="element-628" data-mp="2je8gishw3x1n">
        <Container data-id="element-629" data-mp="2fbbx4zkizwwn">
          <div className="grid gap-12 lg:grid-cols-[0.7fr_1.3fr] lg:gap-16" data-id="element-630" data-mp="2fa0qmrzp4b1u">
            <div data-id="element-631" data-mp="2abqy6p5cvojx">
              <Eyebrow data-id="element-632" data-mp="2ab7f353yyu4b">Preguntas frecuentes</Eyebrow>
              <Heading as="h2" size="md" className="mt-5" data-id="element-633" data-mp="2adtufqx3qdx3">
                Control Tower
              </Heading>
            </div>
            <Faq items={FAQS} data-id="element-634" data-mp="8l5mj49ij2az" />
          </div>
        </Container>
      </section>

      <CtaBand title="Pongamos su operación en una sola pantalla." text="Definimos qué información se consolida, qué indicadores se miden y qué protocolo se activa ante cada desvío." primaryLabel="Solicitar propuesta" location="control-tower-cta" service="Control Tower" data-id="element-635" data-mp="3lj8wtprvk1n" />
    </>;
}